// src/components/MasterMode/MasterMode.tsx
import React, { useState, useEffect, useCallback } from 'react';
import './MasterMode.scss';
import Reservations from './components/Reservations/Reservations';
import Waitlist from './components/Waitlist/Waitlist';
import Sales from './components/Sales/Sales';
import CajaPanel from './components/CajaPanel/CajaPanel';
import { Mesa, PedidoGroup } from '../../../Configuracion/types';
import { Pedido } from '../../types';

type Tab = 'reservations' | 'waitlist' | 'sales' | 'caja';

interface WaitlistData {
  waitlists: any[];
  sorted_mesas: any[];
}

interface MasterModeProps {
  show: boolean;
  reservationsToday?: any[]; // Reemplazar con ReservationDetails[] si se desea tipado estricto
  filteredReservationsInAHour?: any[];
  setReservationsToday?: (reservations: any[]) => void;
  setFilteredReservationsInAHour?: (reservations: any[]) => void;
  setSalones?: React.Dispatch<React.SetStateAction<any[]>>;
  fetchReservations?: () => void;
  waitList?: WaitlistData; // Cambiado de any[] a WaitlistData
  setWaitList?: (waitList: WaitlistData) => void;
  salones?: any[]; // Reemplazar con el tipo adecuado si se desea tipado estricto
  selectedMesa?: Mesa | null;
  selectedGroup?: PedidoGroup | null;
  salonName?: string;
  hosterMode?: boolean;
  selectedPedido?: Pedido | null;
  viewPresentation?: 'mapa' | 'lista' | 'pedidos' ;
  onClearPedidoSelection?: () => void;
  onClearGroupSelection?: () => void;
  onClearMesaSelection?: () => void;
  onRefetchPedidos?: () => Promise<void>;
  activeTab?: Tab; // External tab control
  setActiveTab?: (tab: Tab) => void; // External tab setter
  onMesaClosed?: (mesaId: number) => void; // Callback when mesa is closed
}

const MasterMode: React.FC<MasterModeProps> = ({ 
  show,
  reservationsToday = [], 
  filteredReservationsInAHour = [], 
  setReservationsToday, 
  setFilteredReservationsInAHour, 
  setSalones, 
  fetchReservations, 
  waitList = { waitlists: [], sorted_mesas: [] }, 
  setWaitList, 
  salones,
  selectedMesa,
  selectedGroup,
  salonName,
  hosterMode = false,
  selectedPedido,
  viewPresentation = 'mapa',
  onClearPedidoSelection,
  onClearGroupSelection,
  onClearMesaSelection,
  onRefetchPedidos,
  activeTab: externalActiveTab,
  setActiveTab: externalSetActiveTab,
  onMesaClosed }) => {
  
  // Detect if we're on mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1400);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1400);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Use external tab control if provided, otherwise use internal state
  const [internalActiveTab, setInternalActiveTab] = useState<Tab>((hosterMode && !isMobile) ? 'sales' : 'reservations');
  const activeTab = externalActiveTab ?? internalActiveTab;
  
  // Wrap setActiveTab to ensure stable reference for useEffect dependencies
  const setActiveTab = useCallback((tab: Tab) => {
    if (externalSetActiveTab) {
      externalSetActiveTab(tab);
    } else {
      setInternalActiveTab(tab);
    }
  }, [externalSetActiveTab]);
  
  // If switching to mobile, ensure we're not on the sales tab
  useEffect(() => {
    if (isMobile && activeTab === 'sales') {
      setActiveTab('reservations');
    }
  }, [isMobile, activeTab, setActiveTab]);

  // When a table or group is selected, automatically switch to sales tab (if in hosterMode and not mobile)
  useEffect(() => {
    if ((selectedMesa || selectedGroup) && hosterMode && !isMobile) {
      setActiveTab('sales');
    }
  }, [selectedMesa, selectedGroup, hosterMode, isMobile, setActiveTab]);

  // When switching to pedidos view, automatically switch to sales tab
  useEffect(() => {
    if (viewPresentation === 'pedidos' && hosterMode && !isMobile) {
      setActiveTab('sales');
    }
  }, [viewPresentation, hosterMode, isMobile, setActiveTab]);

  return (
    <div className="master-mode">
      <div className="master-mode__tabs">
        {/* Only show Sales tab on desktop when hosterMode is active */}
        {hosterMode && !isMobile && (
          <button
            className={activeTab === 'sales' ? 'active' : ''}
            onClick={() => setActiveTab('sales')}
          >
            Venta
          </button>
        )}
        {/**     */}
        <button
          className={activeTab === 'reservations' ? 'active' : ''}
          onClick={() => setActiveTab('reservations')}
        >
          Reservas
        </button>
     
         <button
          className={activeTab === 'caja' ? 'active' : ''}
          onClick={() => setActiveTab('caja')}
        >
          Caja
        </button>
        <button
          className={activeTab === 'waitlist' ? 'active' : ''}
          onClick={() => setActiveTab('waitlist')}
        >
          Lista de espera
        </button>
      </div>

      <div className="master-mode__panel">
        {activeTab === 'sales' && hosterMode && !isMobile && (
          <Sales 
            selectedMesa={selectedMesa ?? null}
            selectedGroup={selectedGroup ?? null}
            salonName={salonName}
            salones={salones}
            selectedPedido={selectedPedido ?? null}
            viewPresentation={viewPresentation}
            onClearPedidoSelection={onClearPedidoSelection}
            onClearGroupSelection={onClearGroupSelection}
            onClearMesaSelection={onClearMesaSelection}
            onRefetchPedidos={onRefetchPedidos}
            onMesaClosed={onMesaClosed}
          />
        )}
        {activeTab === 'caja' && (
          <CajaPanel isActive={activeTab === 'caja'} />
        )}
        {activeTab === 'reservations' && (
          <Reservations 
            reservationsToday={reservationsToday}
            filteredReservationsInAHour={filteredReservationsInAHour}
            setReservationsToday={setReservationsToday}
            setFilteredReservationsInAHour={setFilteredReservationsInAHour}
            salones={salones}
            setSalones={setSalones}
            fetchReservations={fetchReservations}
          />
        )}
        {activeTab === 'waitlist' && (
          <Waitlist
            waitList={waitList}
            setWaitList={setWaitList}
          />
        )}
      </div>
    </div>
  );
};

export default MasterMode;
